/**
 * Routen und global sichtbare Modals.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Shell } from './komponenten/Shell';
import { Login } from './seiten/Login';
import { MeinTag } from './seiten/MeinTag';
import { Cockpit } from './seiten/Cockpit';
import { AngebotDetail } from './seiten/AngebotDetail';
import { Kundenstamm } from './seiten/Kundenstamm';
import { Finanzen } from './seiten/Finanzen';
import { Auftraege } from './seiten/Auftraege';
import { Auftragsabwicklung } from './seiten/Auftragsabwicklung';
import { Montagekalender } from './seiten/Montagekalender';
import { NeuesAngebot } from './seiten/NeuesAngebot';
import { Vorschlaege } from './seiten/Vorschlaege';
import { VorschlagDetail } from './seiten/VorschlagDetail';
import { Buchhaltungsvorbereitung } from './seiten/Buchhaltungsvorbereitung';
import { AdminCenterModal } from './modals/AdminCenterModal';
import { ProfilModal } from './modals/ProfilModal';
import { ChangelogModal } from './modals/ChangelogModal';
import { useKora } from './zustand';

export function App() {
  const { angemeldet, ladend, meta, nutzer, modal, modalOeffnen } = useKora();
  const ort = useLocation();

  // Hinweis auf eine neue Version: einmal pro Nutzer und Version.
  useEffect(() => {
    if (!nutzer || !meta || modal) return;
    const neueste = meta.changelog[0]?.version;
    if (neueste && nutzer.gesehenVersion !== neueste) {
      modalOeffnen({ art: 'changelog', alle: false });
    }
    // Absichtlich nur an Nutzer und Meta gebunden – sonst öffnet sich das Fenster erneut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nutzer?.id, meta?.version]);

  /*
   * Bildschirm heißt: entweder das Anzeige-Konto oder jemand, der die
   * Ansicht mit `?anzeige=1` zum Prüfen aufruft (6.5).
   */
  const bildschirm =
    !!nutzer?.nurAnzeige ||
    (ort.pathname === '/montagekalender' && new URLSearchParams(ort.search).get('anzeige') === '1');

  if (ladend) {
    return (
      <div className="app-laden">
        <span>Kora wird geladen …</span>
      </div>
    );
  }

  if (!angemeldet) return <Login />;

  /*
   * Die Bildschirm-Ansicht läuft ohne Shell (MONTAGEKALENDER.md 6.3): kein
   * Hauptmenü, kein Chat, keine Kopfzeile. Für ein Anzeige-Konto führt
   * außerdem jede Adresse dorthin (6.5) – das Gerät an der Wand soll auch
   * nach einem versehentlichen Klick wieder am Kalender stehen.
   */
  if (bildschirm) {
    return (
      <Routes>
        <Route path="/montagekalender" element={<Montagekalender />} />
        <Route path="*" element={<Navigate to="/montagekalender" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <Shell>
        <Routes>
          <Route path="/" element={<MeinTag />} />
          {/* Arbeits-Sicht für das ganze Team – ohne Bereichsrecht, ohne Beträge. */}
          <Route path="/abwicklung" element={<Auftragsabwicklung />} />
          <Route
            path="/montagekalender"
            element={<Geschuetzt bereich="montage" element={<Montagekalender />} />}
          />
          <Route path="/cockpit" element={<Geschuetzt bereich="monitor" element={<Cockpit />} />} />
          <Route
            path="/angebot/:id"
            element={<Geschuetzt bereich="monitor" element={<AngebotDetail />} />}
          />
          <Route path="/neu" element={<Geschuetzt bereich="monitor" element={<NeuesAngebot />} />} />
          <Route
            path="/kunden"
            element={<Geschuetzt bereich="kunden" element={<Kundenstamm />} />}
          />
          <Route
            path="/auftraege"
            element={<Geschuetzt bereich="auftraege" element={<Auftraege />} />}
          />
          <Route
            path="/finanzen"
            element={<Geschuetzt bereich="finanzen" element={<Finanzen />} />}
          />
          <Route
            path="/buchhaltung"
            element={<Geschuetzt bereich="buchhaltung" element={<Buchhaltungsvorbereitung />} />}
          />
          {/*
            Vorschläge ohne Bereichsprüfung: Jeder darf einbringen und alles
            sehen. Entschieden wird trotzdem nur vom Administrator – das prüft
            der Server, nicht die Navigation.
          */}
          <Route path="/vorschlaege" element={<Vorschlaege />} />
          <Route path="/vorschlaege/:id" element={<VorschlagDetail />} />
          {/*
            Der Menüpunkt „sevDesk-Abgleich" ist entfallen (v12). Wer die
            Adresse noch als Lesezeichen hat, landet dort, wo die Arbeit
            jetzt stattfindet – im Kundenstamm mit gesetztem Filter, nicht
            wortlos auf „Mein Tag".
          */}
          <Route path="/kunden/sevdesk" element={<Navigate to="/kunden?ohneSevdesk=1" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>

      {/*
        Kora schwebt nicht mehr.

        Bis v7 saß sie als Pille unten rechts, außerhalb der Shell: ein fester
        Platz, aber über dem Inhalt, nur wegklickbar und nach dem nächsten
        Laden wieder da. Seit v11 (10.2 Regel 6) steht sie unter dem Inhalt
        der jeweiligen Maske – `KoraMaskenhinweise` – mit Stufen, „Erledigt"
        und Auto-Erledigen über den genannten Vorgang.
      */}

      {modal?.art === 'adminCenter' && <AdminCenterModal />}
      {modal?.art === 'profil' && <ProfilModal />}
      {modal?.art === 'changelog' && <ChangelogModal alle={modal.alle} />}
    </>
  );
}

/** Blendet einen Bereich aus, solange er nicht freigeschaltet ist. */
function Geschuetzt({ bereich, element }: { bereich: string; element: React.ReactElement }) {
  const { darf } = useKora();
  if (!darf(bereich)) {
    return (
      <div className="eh-inhalt">
        <div className="eh-karte">
          <div className="eh-titel" style={{ fontSize: 24 }}>
            Kein Zugriff auf diesen Bereich
          </div>
          <p className="eh-hinweis" style={{ marginTop: 10 }}>
            Die Geschäftsführung kann den Bereich im Admin Center freischalten.
          </p>
        </div>
      </div>
    );
  }
  return element;
}
